/**
 * Agency Command Center — Operational Dashboard for Leadership
 * 
 * A customizable grid dashboard showing real-time operational data:
 * - Today at a Glance
 * - Active Visits
 * - Needs Attention (running late, call-offs, missed clock-ins)
 * - Open Shifts
 * - Pending Reviews
 * - Communications
 * - Alerts
 * - Payroll/Billing Progress
 * 
 * Philosophy: This is the cockpit for agency operations.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  LayoutDashboard, Activity, AlertTriangle, Clock, Calendar, 
  MessageSquare, Bell, DollarSign, Users, RefreshCw, Settings,
  ChevronRight, ExternalLink, Phone, MapPin, FileText, CheckCircle2,
  TrendingUp, Briefcase, Grip
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtime, EVENT_CATEGORIES } from '../../contexts/RealtimeContext';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';

// Default panel configuration
const DEFAULT_PANELS = [
  { id: 'today_at_glance', title: 'Today at a Glance', icon: LayoutDashboard, gridArea: 'today', size: 'medium' },
  { id: 'needs_attention', title: 'Needs Attention', icon: AlertTriangle, gridArea: 'attention', size: 'medium' },
  { id: 'active_visits', title: 'Active Visits', icon: Activity, gridArea: 'active', size: 'large' },
  { id: 'open_shifts', title: 'Open Shifts', icon: Calendar, gridArea: 'shifts', size: 'medium' },
  { id: 'pending_reviews', title: 'Pending Reviews', icon: FileText, gridArea: 'reviews', size: 'small' },
  { id: 'communications', title: 'Communications', icon: MessageSquare, gridArea: 'comms', size: 'medium' },
  { id: 'alerts', title: 'Alerts', icon: Bell, gridArea: 'alerts', size: 'medium' },
  { id: 'payroll_billing', title: 'Payroll & Billing', icon: DollarSign, gridArea: 'payroll', size: 'small' },
];

export default function CommandCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Subscribe to real-time events for auto-updates
  useRealtime({
    categories: [
      EVENT_CATEGORIES.VISIT,
      EVENT_CATEGORIES.SCHEDULE,
      EVENT_CATEGORIES.COMMUNICATION,
      EVENT_CATEGORIES.ALERT,
    ],
    onEvent: (event) => {
      // Refresh relevant panel when event occurs
      console.log('[CommandCenter] Event received:', event.event_type);
      if (autoRefresh) {
        fetchData();
      }
    },
  });
  
  // Fetch dashboard data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/command-center');
      setData(response.data);
      setLastUpdated(new Date());
      
    } catch (err) {
      console.error('Failed to fetch command center data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Initial load and auto-refresh
  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 60 seconds
    const interval = autoRefresh ? setInterval(fetchData, 60000) : null;
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchData, autoRefresh]);
  
  // Format time ago
  const formatTimeAgo = (date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return date.toLocaleTimeString();
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="command-center">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
                <LayoutDashboard className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Command Center</h1>
                <p className="text-sm text-gray-500">Operational Dashboard</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {lastUpdated && (
                <span className="text-sm text-gray-500">
                  Updated {formatTimeAgo(lastUpdated)}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                />
                Auto-refresh
              </label>
            </div>
          </div>
        </div>
      </div>
      
      {/* Dashboard Grid */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-gray-600">{error}</p>
            <Button variant="outline" onClick={fetchData} className="mt-4">
              Retry
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Row 1: Key Metrics */}
            <TodayAtGlancePanel data={data?.panels?.today_at_glance} loading={loading} />
            <NeedsAttentionPanel data={data?.panels?.needs_attention} loading={loading} onNavigate={navigate} />
            
            {/* Row 2: Active Operations */}
            <ActiveVisitsPanel data={data?.panels?.active_visits} loading={loading} />
            <OpenShiftsPanel data={data?.panels?.open_shifts} loading={loading} onNavigate={navigate} />
            
            {/* Row 3: Reviews & Communications */}
            <PendingReviewsPanel data={data?.panels?.pending_reviews} loading={loading} onNavigate={navigate} />
            <CommunicationsPanel data={data?.panels?.communications} loading={loading} onNavigate={navigate} />
            
            {/* Row 4: Alerts & Progress */}
            <AlertsPanel data={data?.panels?.alerts} loading={loading} />
            <PayrollBillingPanel data={data?.panels?.payroll_billing} loading={loading} />
          </div>
        )}
      </div>
    </div>
  );
}

// ============ PANEL COMPONENTS ============

function PanelWrapper({ title, icon: Icon, children, loading, className = '', headerAction }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${className}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-gray-400" />
          <h3 className="font-medium text-gray-900">{title}</h3>
        </div>
        {headerAction}
      </div>
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function TodayAtGlancePanel({ data, loading }) {
  if (!data) return <PanelWrapper title="Today at a Glance" icon={LayoutDashboard} loading={loading}><div /></PanelWrapper>;
  
  return (
    <PanelWrapper title="Today at a Glance" icon={LayoutDashboard} loading={loading}>
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          label="Total Visits"
          value={data.total_visits || 0}
          icon={Calendar}
          color="blue"
        />
        <StatCard
          label="Completed"
          value={data.completed || 0}
          icon={CheckCircle2}
          color="green"
          subtext={data.completion_rate ? `${data.completion_rate}%` : null}
        />
        <StatCard
          label="In Progress"
          value={data.in_progress || 0}
          icon={Activity}
          color="purple"
        />
        <StatCard
          label="Remaining"
          value={data.remaining || 0}
          icon={Clock}
          color="gray"
        />
      </div>
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 text-sm">
        <div className="flex items-center gap-1 text-gray-500">
          <Users className="w-4 h-4" />
          <span>{data.active_clients || 0} clients</span>
        </div>
        <div className="flex items-center gap-1 text-gray-500">
          <Briefcase className="w-4 h-4" />
          <span>{data.active_caregivers || 0} caregivers</span>
        </div>
      </div>
    </PanelWrapper>
  );
}

function StatCard({ label, value, icon: Icon, color, subtext }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600',
    orange: 'bg-orange-50 text-orange-600',
    gray: 'bg-gray-50 text-gray-600',
  };
  
  return (
    <div className="p-3 rounded-lg bg-gray-50">
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-2xl font-bold text-gray-900">{value}</span>
        {subtext && <span className="text-sm text-gray-500">{subtext}</span>}
      </div>
      <span className="text-xs text-gray-500">{label}</span>
    </div>
  );
}

function NeedsAttentionPanel({ data, loading, onNavigate }) {
  if (!data) return <PanelWrapper title="Needs Attention" icon={AlertTriangle} loading={loading}><div /></PanelWrapper>;
  
  const hasAttention = data.total_attention_needed > 0;
  
  return (
    <PanelWrapper 
      title="Needs Attention" 
      icon={AlertTriangle} 
      loading={loading}
      headerAction={hasAttention && (
        <Badge className="bg-red-100 text-red-700">{data.total_attention_needed}</Badge>
      )}
    >
      <div className="space-y-3">
        {/* Running Late */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-orange-50 border border-orange-100">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            <span className="font-medium text-orange-800">Running Late</span>
          </div>
          <span className="text-lg font-bold text-orange-700">{data.running_late?.count || 0}</span>
        </div>
        
        {/* Missed Clock-Ins */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-100">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="font-medium text-red-800">Missed Clock-In</span>
          </div>
          <span className="text-lg font-bold text-red-700">{data.missed_clock_ins?.count || 0}</span>
        </div>
        
        {/* Call-Offs */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-yellow-50 border border-yellow-100">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-yellow-600" />
            <span className="font-medium text-yellow-800">Call-Offs</span>
          </div>
          <span className="text-lg font-bold text-yellow-700">{data.call_offs?.count || 0}</span>
        </div>
        
        {/* Quick list of running late */}
        {data.running_late?.items?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-2">Running late:</p>
            {data.running_late.items.slice(0, 3).map((item, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1">
                <span className="text-gray-700">{item.caregiver_name}</span>
                <span className="text-orange-600">{item.minutes_late}m late</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </PanelWrapper>
  );
}

function ActiveVisitsPanel({ data, loading }) {
  if (!data) return <PanelWrapper title="Active Visits" icon={Activity} loading={loading} className="md:col-span-2"><div /></PanelWrapper>;
  
  return (
    <PanelWrapper 
      title="Active Visits" 
      icon={Activity} 
      loading={loading}
      className="md:col-span-2"
      headerAction={<Badge className="bg-green-100 text-green-700">{data.count || 0} active</Badge>}
    >
      {data.count === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>No active visits right now</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {data.visits?.map((visit, i) => (
            <div 
              key={i}
              className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div>
                <div className="font-medium text-gray-900">{visit.caregiver_name}</div>
                <div className="text-sm text-gray-500">with {visit.client_name}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-green-600">In Progress</div>
                <div className="text-xs text-gray-500">{visit.duration_minutes}m</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </PanelWrapper>
  );
}

function OpenShiftsPanel({ data, loading, onNavigate }) {
  if (!data) return <PanelWrapper title="Open Shifts" icon={Calendar} loading={loading}><div /></PanelWrapper>;
  
  return (
    <PanelWrapper 
      title="Open Shifts" 
      icon={Calendar} 
      loading={loading}
      headerAction={
        data.count > 0 && (
          <Badge className="bg-purple-100 text-purple-700">{data.count}</Badge>
        )
      }
    >
      {data.count === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-40 text-green-500" />
          <p>All shifts covered!</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {data.shifts?.slice(0, 5).map((shift, i) => (
            <div 
              key={i}
              className="flex items-center justify-between p-2 rounded-lg border border-gray-200 hover:border-purple-200 transition-colors cursor-pointer"
              onClick={() => onNavigate('/scheduling')}
            >
              <div>
                <div className="font-medium text-gray-900 text-sm">{shift.client_name}</div>
                <div className="text-xs text-gray-500">{shift.shift_date} • {shift.hours}h</div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
          ))}
          {data.count > 5 && (
            <button 
              className="w-full text-sm text-purple-600 hover:text-purple-800 py-2"
              onClick={() => onNavigate('/scheduling')}
            >
              View all {data.count} open shifts
            </button>
          )}
        </div>
      )}
    </PanelWrapper>
  );
}

function PendingReviewsPanel({ data, loading, onNavigate }) {
  if (!data) return <PanelWrapper title="Pending Reviews" icon={FileText} loading={loading}><div /></PanelWrapper>;
  
  const total = data.total_pending || 0;
  
  return (
    <PanelWrapper 
      title="Pending Reviews" 
      icon={FileText} 
      loading={loading}
      headerAction={total > 0 && <Badge className="bg-amber-100 text-amber-700">{total}</Badge>}
    >
      <div className="space-y-3">
        <div 
          className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
          onClick={() => onNavigate('/coming-soon/care')}
        >
          <span className="text-sm text-gray-700">Care Notes</span>
          <Badge variant="outline">{data.care_notes_pending || 0}</Badge>
        </div>
        <div 
          className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
          onClick={() => onNavigate('/assessments')}
        >
          <span className="text-sm text-gray-700">Assessments</span>
          <Badge variant="outline">{data.assessments_pending || 0}</Badge>
        </div>
        <div 
          className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
          onClick={() => onNavigate('/communications')}
        >
          <span className="text-sm text-gray-700">Acknowledgments</span>
          <Badge variant="outline">{data.communications_requiring_ack || 0}</Badge>
        </div>
      </div>
    </PanelWrapper>
  );
}

function CommunicationsPanel({ data, loading, onNavigate }) {
  if (!data) return <PanelWrapper title="Communications" icon={MessageSquare} loading={loading}><div /></PanelWrapper>;
  
  return (
    <PanelWrapper 
      title="Communications" 
      icon={MessageSquare} 
      loading={loading}
      headerAction={
        <div className="flex items-center gap-2">
          {data.unread_count > 0 && (
            <Badge className="bg-blue-100 text-blue-700">{data.unread_count} unread</Badge>
          )}
          {data.high_priority_today > 0 && (
            <Badge className="bg-red-100 text-red-700">{data.high_priority_today} urgent</Badge>
          )}
        </div>
      }
    >
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {data.recent?.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No recent communications</p>
          </div>
        ) : (
          data.recent?.slice(0, 4).map((comm, i) => (
            <div 
              key={i}
              className="p-2 rounded-lg border border-gray-200 hover:border-blue-200 transition-colors cursor-pointer"
              onClick={() => onNavigate(`/communications/${comm.id}`)}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-900 truncate">{comm.sender_name}</span>
                <span className="text-xs text-gray-500">{new Date(comm.created_at).toLocaleTimeString()}</span>
              </div>
              <p className="text-xs text-gray-600 truncate">{comm.content_preview}</p>
            </div>
          ))
        )}
      </div>
      <button 
        className="w-full text-sm text-blue-600 hover:text-blue-800 py-2 mt-2 border-t border-gray-100"
        onClick={() => onNavigate('/communications')}
      >
        View all communications
      </button>
    </PanelWrapper>
  );
}

function AlertsPanel({ data, loading }) {
  if (!data) return <PanelWrapper title="Alerts" icon={Bell} loading={loading}><div /></PanelWrapper>;
  
  const total = data.total_alerts || 0;
  
  return (
    <PanelWrapper 
      title="Alerts" 
      icon={Bell} 
      loading={loading}
      headerAction={total > 0 && <Badge className="bg-red-100 text-red-700">{total}</Badge>}
    >
      <div className="space-y-3">
        {/* Credential Expirations */}
        <div className="p-3 rounded-lg bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Credential Expirations</span>
            <span className="text-lg font-bold text-gray-900">{data.credential_expirations?.count || 0}</span>
          </div>
          {data.credential_expirations?.critical_count > 0 && (
            <Badge className="bg-red-100 text-red-700">
              {data.credential_expirations.critical_count} within 7 days
            </Badge>
          )}
        </div>
        
        {/* Training Due */}
        <div className="p-3 rounded-lg bg-gray-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Training Due</span>
            <span className="text-lg font-bold text-gray-900">{data.training_due?.count || 0}</span>
          </div>
        </div>
        
        {/* Compliance Issues */}
        <div className="p-3 rounded-lg bg-gray-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Compliance Issues</span>
            <span className="text-lg font-bold text-gray-900">{data.compliance_issues || 0}</span>
          </div>
        </div>
      </div>
    </PanelWrapper>
  );
}

function PayrollBillingPanel({ data, loading }) {
  if (!data) return <PanelWrapper title="Payroll & Billing" icon={DollarSign} loading={loading}><div /></PanelWrapper>;
  
  const payroll = data.payroll || {};
  const billing = data.billing || {};
  
  return (
    <PanelWrapper title="Payroll & Billing" icon={DollarSign} loading={loading}>
      <div className="space-y-4">
        {/* Payroll Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Payroll ({payroll.period})</span>
            <span className="text-sm text-gray-500">{payroll.completion_rate || 0}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all"
              style={{ width: `${payroll.completion_rate || 0}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
            <span>{payroll.completed_shifts || 0} / {payroll.total_shifts || 0} shifts</span>
            <span>{payroll.pending_approval || 0} pending</span>
          </div>
        </div>
        
        {/* Billing Summary */}
        <div className="pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Billing</span>
            <TrendingUp className="w-4 h-4 text-green-500" />
          </div>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="p-2 bg-gray-50 rounded">
              <div className="text-lg font-bold text-gray-900">{billing.total_hours || 0}</div>
              <div className="text-xs text-gray-500">Hours</div>
            </div>
            <div className="p-2 bg-gray-50 rounded">
              <div className="text-lg font-bold text-gray-900">{billing.billable_visits || 0}</div>
              <div className="text-xs text-gray-500">Visits</div>
            </div>
          </div>
        </div>
      </div>
    </PanelWrapper>
  );
}
